import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import CreatorNavbar from '../dashboard/CreatorNavbar';
import ProfileSidebar from './ProfileSidebar';
import EnhancedProfileManagement from './EnhancedProfileManagement';
import MyCollabs from './MyCollabs';
import SocialMediaManagement from './SocialMediaManagement';
import SecurityManagement from './SecurityManagement';
import ReviewsManagement from './ReviewsManagement';

const ProfileLayout = () => {
  const { userId } = useParams(); // Get userId from URL params
  const [activeSection, setActiveSection] = useState('my-profile');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSectionChange = (sectionId, path) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu when section changes
    // Future: Could use path for routing
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'my-collabs': return 'My Collaborations';
      case 'my-profile': 
      case 'profile-basic': return 'My Profile';
      case 'social-media': return 'Social Media';
      // case 'subscriptions': return 'Subscriptions'; // DISABLED
      // case 'billing': // DISABLED
      // case 'billing-history': // DISABLED
      // case 'billing-methods': // DISABLED
      // case 'billing-invoices': return 'Billing'; // DISABLED
      // TODO: Enable when Messages feature is implemented
      // case 'messages': return 'Messages';
      // TODO: Enable when Notifications feature is implemented
      // case 'notifications': return 'Notifications';
      // TODO: Enable when Shipments feature is implemented
      // case 'shipments': return 'Shipments';
      case 'reviews': return 'Reviews';
      case 'security': return 'Security';
      case 'privacy': return 'Privacy';
      case 'support':
      case 'help-center':
      case 'contact-us':
      case 'report-issue': return 'Support';
      default: return 'Profile Management';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'my-profile':
      case 'profile-basic':
        return <EnhancedProfileManagement userId={userId} />;
      
      case 'my-collabs':
        return <MyCollabs userId={userId} />;

      case 'social-media':
        return <SocialMediaManagement userId={userId} />;

      // TODO: Enable when Messages feature is implemented
      // case 'messages':
      //   return (
      //     <div className="p-6">
      //       <h2 className="text-2xl font-bold text-gray-900 mb-4">Messages</h2>
      //       <div className="bg-gray-50 rounded-lg p-8 text-center">
      //         <p className="text-gray-600">Message center coming soon...</p>
      //       </div>
      //     </div>
      //   );

      // TODO: Enable when Notifications feature is implemented
      // case 'notifications':
      //   return (
      //     <div className="p-6">
      //       <h2 className="text-2xl font-bold text-gray-900 mb-4">Notifications</h2>
      //       <div className="bg-gray-50 rounded-lg p-8 text-center">
      //         <p className="text-gray-600">Notification center coming soon...</p>
      //       </div>
      //     </div>
      //   );

      /* Billing Section Cases - DISABLED */
      /*
      case 'billing':
      case 'billing-history':
      case 'billing-methods':
      case 'billing-invoices':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Billing & Subscriptions</h2>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">Billing management coming soon...</p>
            </div>
          </div>
        );
      */

      /* Subscriptions Section Case - DISABLED */
      /*
      case 'subscriptions':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscriptions</h2>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">Subscription management coming soon...</p>
            </div>
          </div>
        );
      */

      // TODO: Enable when Shipments feature is implemented
      // case 'shipments':
      //   return (
      //     <div className="p-6">
      //       <h2 className="text-2xl font-bold text-gray-900 mb-4">Shipments</h2>
      //       <div className="bg-gray-50 rounded-lg p-8 text-center">
      //         <p className="text-gray-600">Shipment tracking coming soon...</p>
      //       </div>
      //     </div>
      //   );

      case 'reviews':
        return <ReviewsManagement userId={userId} />;

      case 'security':
        return <SecurityManagement userId={userId} />;

      /* Privacy Section Case - DISABLED */
      /*
      case 'privacy':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy Settings</h2>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">Privacy settings coming soon...</p>
            </div>
          </div>
        );
      */

      /* Support Section Cases - DISABLED */
      /*
      case 'support':
      case 'help-center':
      case 'contact-us':
      case 'report-issue':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Support Center</h2>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">Support center coming soon...</p>
            </div>
          </div>
        );
      */

      default:
        return <EnhancedProfileManagement userId={userId} />;
    }
  };

  return (
    <div className="min-h-screen dashboard-shell">
      {/* Navbar */}
      <CreatorNavbar />
      
      {/* Profile Layout */}
      <div className="pt-16 sm:pt-20">
        <div className="flex h-screen">
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:z-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <ProfileSidebar 
            activeSection={activeSection} 
            onSectionChange={handleSectionChange}
            onClose={() => setIsMobileMenuOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
            <h1 className="text-lg font-semibold text-gray-900">{getSectionTitle()}</h1>
            <div className="w-10 h-6" /> {/* Spacer for centering */}
          </div>

          {/* Desktop Header with Breadcrumb */}
          <div className="hidden lg:block bg-white/90 backdrop-blur border-b border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="section-kicker mb-3">Profile workspace</div>
                <h1 className="text-2xl font-bold text-gray-900">{getSectionTitle()}</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your profile and account settings with a cleaner layout.</p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Profile Management</span>
                <span>›</span>
                <span className="text-gray-900 font-medium">{getSectionTitle()}</span>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-transparent">
            <div className="max-w-7xl mx-auto px-3 py-4 sm:px-4 lg:px-6">
              {renderContent()}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileLayout;
